var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/environment';
import ADD_TENANT_BRAND from 'api/mutations/ADD_TENANT_BRAND';
import REMOVE_TENANT_BRAND from 'api/mutations/REMOVE_TENANT_BRAND';
import UPDATE_TENANT_BRAND from 'api/mutations/UPDATE_TENANT_BRAND';
import GET_CURRENT_TENANT_BRANDS from 'api/queries/GET_CURRENT_TENANT_BRANDS';
export const initialState = {
    items: [],
    pageInfo: {
        hasNextPage: true,
        endCursor: null,
    },
    status: {
        fetch: 'idle',
        add: 'idle',
        update: 'idle',
        remove: 'idle',
    },
    errors: {
        fetch: null,
        add: null,
        update: null,
        remove: null,
    },
    refetchFlags: {
        fetch: false,
        add: false,
        update: false,
        remove: false,
    },
};
export const fetchBrands = createAsyncThunk('brands/fetchBrands', (_, { getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const state = getState();
    const { pageInfo } = state.parameters.brands;
    try {
        const response = yield client.query({
            query: GET_CURRENT_TENANT_BRANDS,
            variables: {
                first: 25,
                after: pageInfo.endCursor,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.me.currentTenant.brands;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addBrand = createAsyncThunk('brands/addBrand', ({ tenantId, name }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const brandData = yield client.mutate({
            mutation: ADD_TENANT_BRAND,
            variables: { tenantId, name },
            fetchPolicy: 'no-cache',
        });
        return brandData.data.addBrand.brand;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const updateBrand = createAsyncThunk('brands/updateBrand', ({ brandId, name }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const brandData = yield client.mutate({
            mutation: UPDATE_TENANT_BRAND,
            variables: { brandId, name },
        });
        return brandData.data.updateBrand.brand;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const removeBrand = createAsyncThunk('brands/removeBrand', (brandId) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield client.mutate({
            mutation: REMOVE_TENANT_BRAND,
            variables: { brandId },
        });
        return brandId;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
const brandsSlice = createSlice({
    name: 'brands',
    initialState,
    reducers: {
        resetBrands(state) {
            state.items = [];
            state.pageInfo = initialState.pageInfo;
            state.status = initialState.status;
            state.errors = initialState.errors;
            state.refetchFlags = initialState.refetchFlags;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBrands.pending, (state) => {
            state.status.fetch = 'loading';
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchBrands.fulfilled, (state, action) => {
            var _a;
            const newBrands = action.payload.edges.map((edge) => edge.node);
            const uniqueBrands = newBrands.filter((newBrand) => !state.items.some((existingBrand) => existingBrand.id === newBrand.id));
            state.items = [...state.items, ...uniqueBrands];
            state.pageInfo = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.pageInfo;
            state.status.fetch = 'succeeded';
        })
            .addCase(fetchBrands.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch = action.error.message || 'Error fetching components';
        })
            .addCase(addBrand.pending, (state) => {
            state.status.add = 'loading';
        })
            .addCase(addBrand.fulfilled, (state, action) => {
            state.items = [
                ...state.items,
                Object.assign(Object.assign({}, action.payload), { canNameBeUpdated: true, canBeRemoved: true }),
            ];
            state.status.add = 'succeeded';
        })
            .addCase(addBrand.rejected, (state, action) => {
            state.status.add = 'failed';
            state.errors.add = action.error.message || 'Failed to add specification';
        })
            .addCase(updateBrand.pending, (state) => {
            state.status.update = 'loading';
        })
            .addCase(updateBrand.fulfilled, (state, action) => {
            const { id, name } = action.payload;
            const itemIndex = state.items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                state.items[itemIndex] = Object.assign(Object.assign({}, state.items[itemIndex]), { name });
            }
            state.status.update = 'succeeded';
        })
            .addCase(updateBrand.rejected, (state, action) => {
            state.status.update = 'failed';
            state.errors.update = action.error.message || 'Failed to add specification';
        })
            .addCase(removeBrand.pending, (state) => {
            state.status.remove = 'loading';
        })
            .addCase(removeBrand.fulfilled, (state, action) => {
            state.items = state.items.filter((order) => order.id !== action.payload);
            state.status.remove = 'succeeded';
        })
            .addCase(removeBrand.rejected, (state, action) => {
            state.status.remove = 'failed';
            state.errors.remove = action.payload;
        });
    },
});
export const { resetBrands } = brandsSlice.actions;
export const selectBrands = (state) => state.parameters.brands.items;
export const selectBrandById = (state, brandId) => state.parameters.brands.items.find((brand) => brand.id === brandId);
export const selectBrandsPageInfo = (state) => state.parameters.brands.pageInfo;
export const selectBrandsStatus = (state) => state.parameters.brands.status;
export const selectBrandsErrors = (state) => state.parameters.brands.errors;
export const selectBrandsRefetchFlags = (state) => state.parameters.brands.refetchFlags;
export default brandsSlice.reducer;
